import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import Layout from "@/components/layout/Layout";
import OptimizedImage from "@/components/OptimizedImage";
import { fetchConfig, submitLead } from "@/lib/api";

export default function FreeGuidePage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [configReady, setConfigReady] = useState(false);

  useEffect(() => {
    fetchConfig()
      .then(() => setConfigReady(true))
      .catch(() => setConfigReady(true)); // Allow submission attempt even if config fails
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      // Ensure session token is available before making the API call
      if (!configReady) {
        await fetchConfig();
      }
      await submitLead({ email, name, source: "free_guide" });
      navigate("/free-guide-thank-you");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Toaster position="top-center" theme="dark" />

      {/* Hero with Lead Capture */}
      <section className="hero">
        <div className="container hero-grid" style={{ alignItems: "center" }}>
          <div>
            <div className="hero-kicker">FREE GUIDE &amp; THREE-DAY TEST</div>
            <h1 className="hero-title">
              WHY DOES THE SAME LIFE <br />
              <span className="text-gold">KEEP FINDING YOU?</span>
            </h1>
            <p className="hero-subtitle">
              Discover the pattern behind the loop—and try a simple three-day experiment to see what changes when you deliberately redirect your attention.
            </p>

            {/* Lead Capture Form */}
            <div style={{ background: "var(--navy-card)", border: "1px solid var(--border-gold)", borderRadius: "var(--radius-lg)", padding: "32px", boxShadow: "var(--shadow-card)", maxWidth: "500px" }}>
              <div className="font-serif" style={{ fontSize: "1.4rem", color: "#FFF", fontWeight: 700, marginBottom: "6px" }}>Get "The Loop You Live In" (PDF)</div>
              <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: "20px" }}>Includes the practical Three-Day Test for immediate self-reflection.</p>

              <form onSubmit={handleSubmit} noValidate>
                <div style={{ marginBottom: "16px" }}>
                  <label htmlFor="fg-first-name" style={{ display: "block", fontSize: "0.825rem", color: "var(--text-secondary)", marginBottom: "6px" }}>First Name</label>
                  <input
                    id="fg-first-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your first name"
                    style={{ width: "100%", padding: "14px 16px", borderRadius: "4px", background: "rgba(7,17,31,0.8)", border: "1px solid var(--border-glass)", color: "#FFF", fontSize: "0.95rem" }}
                  />
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label htmlFor="fg-email" style={{ display: "block", fontSize: "0.825rem", color: "var(--text-secondary)", marginBottom: "6px" }}>Email Address</label>
                  <input
                    id="fg-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    style={{ width: "100%", padding: "14px 16px", borderRadius: "4px", background: "rgba(7,17,31,0.8)", border: "1px solid var(--border-glass)", color: "#FFF", fontSize: "0.95rem" }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary-gold btn-lg"
                  style={{ width: "100%", opacity: loading ? 0.6 : 1 }}
                >
                  {loading ? "Sending…" : "SEND ME THE FREE GUIDE →"}
                </button>
              </form>

              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "14px", textAlign: "center", lineHeight: 1.5 }}>
                By requesting the guide, you agree to receive occasional emails from Rajesh Sivacharya. Unsubscribe anytime.
              </div>
            </div>
          </div>

          {/* Free Guide Visual */}
          <div style={{ textAlign: "center" }}>
            <OptimizedImage
              src="/assets/lead-magnet/the-loop-you-live-in.jpg"
              alt="The Loop You Live In — a free guide by Rajesh Sivacharya. Understand your patterns, see what's really driving your life, and try a simple 3-day exercise."
              width={1024}
              height={683}
              style={{ width: "100%", height: "auto", borderRadius: "var(--radius-lg)", border: "1px solid var(--border-gold)", boxShadow: "0 20px 50px rgba(0,0,0,0.6)" }}
            />
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="container section-padding" style={{ borderTop: "1px solid var(--border-glass)" }}>
        <div style={{ textAlign: "center", maxWidth: "720px", margin: "0 auto 48px auto" }}>
          <div className="hero-kicker">INSIDE THE GUIDE</div>
          <h2 style={{ fontSize: "2.6rem" }}>What You Will Discover</h2>
        </div>

        <div className="grid-3">
          <div className="product-card">
            <div style={{ fontFamily: "var(--font-serif)", fontSize: "1.8rem", color: "var(--gold-warm)", fontWeight: 700, marginBottom: "8px" }}>01</div>
            <h3 style={{ fontSize: "1.3rem", marginBottom: "12px" }}>The Repeating Pattern Problem</h3>
            <p style={{ fontSize: "0.9rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
              Why intelligent people repeat choices they explicitly want to change, and why willpower alone inevitably resets you to old habits.
            </p>
          </div>

          <div className="product-card">
            <div style={{ fontFamily: "var(--font-serif)", fontSize: "1.8rem", color: "var(--gold-warm)", fontWeight: 700, marginBottom: "8px" }}>02</div>
            <h3 style={{ fontSize: "1.3rem", marginBottom: "12px" }}>The Attention &amp; Filter Concept</h3>
            <p style={{ fontSize: "0.9rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
              How your subconscious attention filters out potential opportunities and locks your focus onto old, familiar identity narratives.
            </p>
          </div>

          <div className="product-card">
            <div style={{ fontFamily: "var(--font-serif)", fontSize: "1.8rem", color: "var(--gold-warm)", fontWeight: 700, marginBottom: "8px" }}>03</div>
            <h3 style={{ fontSize: "1.3rem", marginBottom: "12px" }}>The Three-Day Test</h3>
            <p style={{ fontSize: "0.9rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
              A simple, practical 3-day reflection exercise designed to help you observe one specific repeating loop without judgment.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
